import { chunk, debounce, trim, uniqBy } from 'lodash-es'
import { defineAsyncComponent, markRaw } from 'vue'

import Checkbox from 'src/components/checkbox/checkbox.vue'
import Popover from 'src/components/popover/popover.vue'
import UnicodeEmoji from 'src/components/unicode_emoji/unicode_emoji.vue'
import { ensureFinalFallback } from '../../i18n/languages.js'

import { useEmojiStore } from 'src/stores/emoji.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'

import {
  addRecent,
  loadRecent,
  recentForStrip,
  saveRecent,
} from 'src/services/recent_emoji/recent_emoji.js'

const UNICODE_EMOJI_GROUP_ICON = {
  'smileys-and-emotion': 'smile',
  'people-and-body': 'user',
  'animals-and-nature': 'paw',
  'food-and-drink': 'ice-cream',
  'travel-and-places': 'bus',
  activities: 'basketball-ball',
  objects: 'lightbulb',
  symbols: 'code',
  flags: 'flag',
}

const maybeLocalizedKeywords = (emoji, languages, nameLocalizer) => {
  const res = [emoji.displayText, nameLocalizer(emoji)]
  if (emoji.annotations) {
    languages.forEach((lang) => {
      const keywords = emoji.annotations[lang]?.keywords || []
      const name = emoji.annotations[lang]?.name
      res.push(...keywords.concat([name]).filter(Boolean))
    })
  }
  return res
}

const filterByKeyword = (list, keyword = '', languages, nameLocalizer) => {
  if (keyword === '') return list

  const keywordLowercase = keyword.toLowerCase()
  const orderedEmojiList = []
  for (const emoji of list) {
    const indices = maybeLocalizedKeywords(emoji, languages, nameLocalizer)
      .map((k) => k.toLowerCase().indexOf(keywordLowercase))
      .filter((k) => k > -1)

    const indexOfKeyword = indices.length ? Math.min(...indices) : -1

    if (indexOfKeyword > -1) {
      if (!Array.isArray(orderedEmojiList[indexOfKeyword])) {
        orderedEmojiList[indexOfKeyword] = []
      }
      orderedEmojiList[indexOfKeyword].push(emoji)
    }
  }
  return orderedEmojiList.flat()
}

const getOffset = (elem) => {
  const style = elem.style.transform
  const res = /translateY\((\d+)px\)/.exec(style)
  if (!res) {
    return 0
  }
  return res[1]
}

const toHeaderId = (id) => {
  return id.replace(/^row-\d+-/, '')
}

// the strip's search shows this many columns and at most this many results
const QUICK_COLUMNS = 7
const QUICK_RESULTS = QUICK_COLUMNS * 5

const emojiKey = (e) => (e.imageUrl ? `:${e.displayText}:` : e.replacement)

let scrollerModule = null
const loadScroller = () => {
  scrollerModule ??= import('./virtual_scroller.js').then(
    ({ DynamicScroller, DynamicScrollerItem }) =>
      markRaw({ DynamicScroller, DynamicScrollerItem }),
  )
  return scrollerModule
}

const EmojiPicker = {
  props: {
    enableStickerPicker: {
      required: false,
      type: Boolean,
      default: true,
    },
    hideCustomEmoji: {
      required: false,
      type: Boolean,
      default: false,
    },
    // open as a strip of recent emoji that expands to the full picker
    quick: {
      required: false,
      type: Boolean,
      default: false,
    },
    // passed to the popover: 'start' puts its left edge at the anchor
    align: String,
    // keep the popover within the closest element matching this
    boundToSelector: String,
  },
  inject: {
    popoversZLayer: {
      default: '',
    },
  },
  data() {
    return {
      keyword: '',
      activeGroup: 'custom',
      showingStickers: false,
      groupsScrolledClass: 'scrolled-top',
      keepOpen: false,
      hideCustomEmojiInPicker: false,
      // Lazy-load only after the first time `showing` becomes true.
      contentLoaded: false,
      popoverShown: false,
      groupRefs: {},
      filteredEmojiGroups: [],
      emojiSize: 0,
      width: 0,
      expanded: false,
      // read on every open: other pickers add to it meanwhile
      recent: [],
      // the list scroller, loaded before the full picker first shows
      scroller: null,
      // the strip's own search, carried over when it expands
      stripKeyword: '',
      quickActive: 0,
    }
  },
  components: {
    UnicodeEmoji,
    StickerPicker: defineAsyncComponent(
      () => import('src/components/sticker_picker/sticker_picker.vue'),
    ),
    Checkbox,

    Popover,
  },
  methods: {
    emojiKey,
    groupScroll(e) {
      e.currentTarget.scrollLeft += e.deltaY + e.deltaX
    },
    updateEmojiSize() {
      const css = window.getComputedStyle(this.$refs.popover.$el)
      const fontSize = css.getPropertyValue('font-size') || '1rem'
      const emojiSize = css.getPropertyValue('--emojiSize') || '2.2rem'

      const fontSizeUnit = fontSize.replace(/[0-9,.]+/, '').trim()
      const fontSizeValue = Number(fontSize.replace(/[^0-9,.]+/, ''))

      const emojiSizeUnit = emojiSize.replace(/[0-9,.]+/, '').trim()
      const emojiSizeValue = Number(emojiSize.replace(/[^0-9,.]+/, ''))

      let fontSizeMultiplier
      if (fontSizeUnit.endsWith('em')) {
        fontSizeMultiplier = fontSizeValue
      } else {
        fontSizeMultiplier = fontSizeValue / 14
      }

      let emojiSizeReal
      if (emojiSizeUnit.endsWith('em')) {
        emojiSizeReal = emojiSizeValue * fontSizeMultiplier * 14
      } else {
        emojiSizeReal = emojiSizeValue
      }

      const fullEmojiSize = emojiSizeReal + 2 * 0.2 * fontSizeMultiplier * 14
      this.emojiSize = fullEmojiSize
    },
    togglePicker() {
      if (this.popoverShown) {
        this.hidePicker()
      } else {
        this.showPicker()
      }
    },
    // the full list needs its scroller before the popover measures itself
    loadScroller() {
      return loadScroller().then((scroller) => {
        this.scroller = scroller
      })
    },
    // the full list: its scroller and the unicode emoji data
    loadFullList() {
      return Promise.all([
        this.loadScroller(),
        useEmojiStore().ensureUnicodeEmoji(),
      ])
    },
    async showPicker() {
      this.recent = loadRecent()
      if (this.showingStrip) {
        this.$refs.popover.showPopover()
        this.loadFullList() // most likely wanted next
        return
      }
      await this.loadFullList()
      if (!this.$refs.popover) return // gone while loading
      this.$refs.popover.showPopover()
      this.$nextTick(() => {
        this.onShowing()
      })
    },
    async expand() {
      await this.loadFullList()
      if (!this.popoverShown) return // closed while loading
      this.expanded = true
      this.$nextTick(() => {
        this.keyword = this.stripKeyword
        this.$refs.popover.updateStyles()
        this.onShowing()
      })
    },
    hidePicker() {
      this.$refs.popover.hidePopover()
    },
    setAnchorEl(el) {
      this.$refs.popover.setAnchorEl(el)
    },
    setGroupRef(name) {
      return (el) => {
        this.groupRefs[name] = el
      }
    },
    onPopoverShown() {
      this.popoverShown = true
      // a touch keyboard would cover half the screen on every reaction
      if (window.matchMedia?.('(pointer: coarse)').matches) return
      this.$nextTick(() => this.$refs.quickSearch?.focus())
    },
    onPopoverClosed() {
      this.popoverShown = false
      this.expanded = false
      this.stripKeyword = ''
      this.quickActive = 0
    },
    onQuickKey(event) {
      // Enter and arrows belong to the input method while it composes
      if (event.isComposing || event.keyCode === 229) return
      const moves = {
        ArrowRight: 1,
        ArrowLeft: -1,
        ArrowDown: QUICK_COLUMNS,
        ArrowUp: -QUICK_COLUMNS,
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        if (this.stripKeyword) {
          this.stripKeyword = ''
          this.quickActive = 0
        } else {
          this.hidePicker()
        }
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const emoji = this.searching && this.quickItems[this.quickActive]
        if (emoji) this.onEmoji(emoji)
      } else if (this.searching && moves[event.key]) {
        event.preventDefault()
        const next = this.quickActive + moves[event.key]
        if (next >= 0 && next < this.quickItems.length) this.quickActive = next
      }
    },
    onStickerUploaded(e) {
      this.$emit('sticker-uploaded', e)
    },
    onStickerUploadFailed(e) {
      this.$emit('sticker-upload-failed', e)
    },
    onEmoji(emoji) {
      const value = emojiKey(emoji)
      // the strip is a quick pick: it always closes
      const keepOpen = this.keepOpen && !this.showingStrip
      if (!keepOpen) {
        this.$refs.popover.hidePopover()
      }
      this.recent = addRecent(loadRecent(), emoji)
      saveRecent(this.recent)
      this.$emit('emoji', {
        insertion: value,
        insertionUrl: emoji.imageUrl,
        keepOpen,
      })
    },
    onScroll(startIndex, endIndex, visibleStartIndex, visibleEndIndex) {
      const target = this.$refs['emoji-groups']?.$el
      if (!target) return // the list is not drawn yet
      this.scrolledGroup(target, visibleStartIndex, visibleEndIndex)
    },
    scrolledGroup(target, start, end) {
      const top = target.scrollTop + 5
      this.$nextTick(() => {
        this.emojiItems.slice(start, end + 1).forEach((group) => {
          const headerId = toHeaderId(group.id)
          const ref = this.groupRefs['group-' + group.id]
          if (!ref) {
            return
          }
          const elem = ref.$el.parentElement
          if (!elem) {
            return
          }
          if (elem && getOffset(elem) <= top) {
            this.activeGroup = headerId
          }
        })
        this.scrollHeader()
      })
    },
    scrollHeader() {
      // Scroll the active tab's header into view
      const headerRef = this.groupRefs['group-header-' + this.activeGroup]
      if (!headerRef) return
      const left = headerRef.offsetLeft
      const right = left + headerRef.offsetWidth
      const headerCont = this.$refs.header
      const currentScroll = headerCont.scrollLeft
      const currentScrollRight = currentScroll + headerCont.clientWidth
      const setScroll = (s) => {
        headerCont.scrollLeft = s
      }

      const margin = 7 // .emoji-tabs-item: padding
      if (left - margin < currentScroll) {
        setScroll(left - margin)
      } else if (right + margin > currentScrollRight) {
        setScroll(right + margin - headerCont.clientWidth)
      }
    },
    highlight(groupId) {
      this.setShowStickers(false)
      const indexInList = this.emojiItems.findIndex((k) => k.id === groupId)
      this.$refs['emoji-groups'].scrollToItem(indexInList)
    },
    toggleStickers() {
      this.showingStickers = !this.showingStickers
    },
    setShowStickers(value) {
      this.showingStickers = value
    },
    filterByKeyword(list, keyword) {
      return filterByKeyword(
        list,
        keyword,
        this.languages,
        this.maybeLocalizedEmojiName,
      )
    },
    onShowing() {
      const oldContentLoaded = this.contentLoaded
      this.updateEmojiSize()
      this.recalculateItemPerRow()
      this.$nextTick(() => {
        this.$refs.search?.focus()
      })
      this.contentLoaded = true
      this.filteredEmojiGroups = this.getFilteredEmojiGroups()
      if (!oldContentLoaded) {
        this.$nextTick(() => {
          if (this.defaultGroup) {
            this.highlight(this.defaultGroup)
          }
        })
      }
    },
    getFilteredEmojiGroups() {
      return this.allEmojiGroups
        .map((group) => ({
          ...group,
          emojis: this.filterByKeyword(group.emojis, trim(this.keyword)),
        }))
        .filter((group) => group.emojis.length > 0)
    },
    recalculateItemPerRow() {
      this.$nextTick(() => {
        if (!this.$refs['emoji-groups']) {
          return
        }
        this.width = this.$refs['emoji-groups'].$el.clientWidth
      })
    },
  },
  watch: {
    // results change the strip's height; keep it placed next to its button
    quickRows() {
      this.$nextTick(() => this.$refs.popover?.updateStyles())
    },
    keyword() {
      this.onScroll()
      this.debouncedHandleKeywordChange()
    },
    // custom emoji, or unicode data and names that arrive later
    allEmojiGroups() {
      if (this.contentLoaded) {
        this.filteredEmojiGroups = this.getFilteredEmojiGroups()
      }
    },
  },
  computed: {
    showingStrip() {
      return this.quick && !this.expanded
    },
    stripEmoji() {
      return recentForStrip(this.recent, { custom: !this.hideCustomEmoji })
    },
    searching() {
      return trim(this.stripKeyword) !== ''
    },
    // every emoji once, for the strip's search
    quickPool() {
      return uniqBy(
        this.allEmojiGroups.flatMap((group) => group.emojis),
        emojiKey,
      )
    },
    // search results while searching, else the recent emoji
    quickItems() {
      if (!this.searching) return this.stripEmoji
      return this.filterByKeyword(
        this.quickPool,
        trim(this.stripKeyword),
      ).slice(0, QUICK_RESULTS)
    },
    quickRows() {
      return this.searching
        ? Math.ceil((this.quickItems.length + 1) / QUICK_COLUMNS)
        : 0
    },
    popoverClass() {
      return `emoji-picker popover-default${this.showingStrip ? ' -quick' : ''}`
    },
    minItemSize() {
      return this.emojiSize
    },
    itemPerRow() {
      return this.width ? Math.floor(this.width / this.emojiSize) : 6
    },
    activeGroupView() {
      return this.showingStickers ? '' : this.activeGroup
    },
    allCustomGroups() {
      if (this.hideCustomEmoji || this.hideCustomEmojiInPicker) {
        return {}
      }
      const emojis = useEmojiStore().groupedCustomEmojis
      if (emojis.unpacked) {
        emojis.unpacked.text = this.$t('emoji.unpacked')
      }
      return emojis
    },
    defaultGroup() {
      return Object.keys(this.allCustomGroups)[0]
    },
    unicodeEmojiGroups() {
      return useEmojiStore().standardEmojiGroupList.map((group) => ({
        id: `standard-${group.id}`,
        text: this.$t(`emoji.unicode_groups.${group.id}`),
        icon: UNICODE_EMOJI_GROUP_ICON[group.id],
        emojis: group.emojis,
      }))
    },
    allEmojiGroups() {
      return Object.entries(this.allCustomGroups)
        .map(([, v]) => v)
        .concat(this.unicodeEmojiGroups)
    },
    stickerPickerEnabled() {
      return (useEmojiStore().stickers || []).length !== 0
    },
    debouncedHandleKeywordChange() {
      return debounce(() => {
        this.filteredEmojiGroups = this.getFilteredEmojiGroups()
      }, 500)
    },
    emojiItems() {
      return this.filteredEmojiGroups
        .map((group) =>
          chunk(group.emojis, this.itemPerRow).map((items, index) => ({
            ...group,
            id: index === 0 ? group.id : `row-${index}-${group.id}`,
            emojis: items,
            isFirstRow: index === 0,
          })),
        )
        .flat()
    },
    languages() {
      return ensureFinalFallback(
        useMergedConfigStore().mergedConfig.interfaceLanguage,
      )
    },
    maybeLocalizedEmojiName() {
      return (emoji) => {
        if (!emoji.annotations) {
          return emoji.displayText
        }

        if (emoji.displayTextI18n) {
          return this.$t(emoji.displayTextI18n.key, emoji.displayTextI18n.args)
        }

        for (const lang of this.languages) {
          if (emoji.annotations[lang]?.name) {
            return emoji.annotations[lang].name
          }
        }

        return emoji.displayText
      }
    },
    isInModal() {
      return this.popoversZLayer === 'modals'
    },
  },
}

export default EmojiPicker
